// PrefabTriggeringSection — the "when does this prefab fire" half of the
// prefab editor (ENG-843, per the approved prototype). Rendered by BOTH the
// content-browser PrefabWizard (create) and PrefabDetail (edit) so there is
// one Triggering UI, not two.
//
// Controlled: `phrases` / `notFor` are string arrays owned by the host form
// (they bind 1:1 to convorcher's prefabs.phrases / not_for jsonb columns and
// reach the server through prefabReflection — never through AAM).
//
// The live tester runs the real matcher rule (prefabMatcher.js, a verified
// port of convorcher's KeywordsMatchPrefab): the chip that fires turns green
// with hits/total, exclusion chips that would suppress turn red. Exclusion
// suppression is the PROPOSED behavior — convorcher stores not_for but does
// not enforce it yet, hence the hint.

import React, { useState } from 'react'
import ChipsInput from './ChipsInput.jsx'
import { evaluateUtterance } from '../lib/prefabMatcher.js'

const GREEN = '#5ec98a'
const RED = '#e05858'
const FG3 = '#6d6f78'

const labelStyle = {
  display: 'block', fontSize: 9.5, fontFamily: 'var(--m-font)', color: '#a8aab2',
  letterSpacing: '.14em', textTransform: 'uppercase', margin: '10px 0 4px',
}
const hintStyle = {
  fontSize: 9.5, fontFamily: 'var(--m-font)', color: FG3, letterSpacing: '.04em',
  marginTop: 4, lineHeight: 1.6,
}

function stateMap(indices, state) {
  const map = {}
  for (const i of indices) map[i] = state
  return map
}

// Section rule for the prefab editors: "PLAYBACK — stored in AAM" /
// "TRIGGERING — synced to convorcher". The storage note is load-bearing UX —
// the two halves of a prefab live on different servers (see prefabReflection).
export function PrefabSectionHeader({ name, note, first = false }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8,
      borderTop: first ? 'none' : '1px solid rgba(255,255,255,.07)',
      margin: first ? '0 0 8px' : '16px 0 8px', paddingTop: first ? 0 : 12,
    }}>
      <span style={{ fontSize: 9.5, fontFamily: 'var(--m-font)', letterSpacing: '.22em', textTransform: 'uppercase', color: FG3 }}>{name}</span>
      {note && (
        <span style={{ fontSize: 9.5, fontFamily: 'var(--m-font)', letterSpacing: '.06em', color: FG3, marginLeft: 'auto' }}>
          {note.prefix} <b style={{ color: '#b9a5ff', fontWeight: 500 }}>{note.target}</b>
        </span>
      )}
    </div>
  )
}

export default function PrefabTriggeringSection({ tag, phrases, notFor, onPhrasesChange, onNotForChange, disabled = false }) {
  const [utterance, setUtterance] = useState('')

  const result = evaluateUtterance(utterance, phrases, notFor)
  const { fired, blocked } = result

  let verdict
  if (!utterance.trim()) {
    verdict = <span style={{ color: FG3 }}>Type a test message above to see whether it would fire.</span>
  } else if (fired && blocked) {
    verdict = (
      <>
        <span style={{ color: RED }}>Suppressed</span>
        <span style={{ color: FG3 }}> — matched “{fired.phrase}” but exclusion “{blocked.phrase}” blocks it</span>
      </>
    )
  } else if (fired) {
    verdict = (
      <>
        <span style={{ color: GREEN }}>Fires {tag || 'this prefab'}</span>
        <span style={{ color: FG3 }}> — “{fired.phrase}” matched {fired.hits}/{fired.total} words</span>
      </>
    )
  } else {
    verdict = <span style={{ color: FG3 }}>No match — no phrase reached 50% word overlap</span>
  }

  return (
    <div>
      <label style={labelStyle}>Trigger phrases — what a user might say</label>
      <ChipsInput
        values={phrases}
        onChange={onPhrasesChange}
        placeholder="e.g. give me a high five — press Enter to add"
        ariaLabel="Add trigger phrase"
        variant="accent"
        chipStates={stateMap(result.phraseHits, 'hit')}
        disabled={disabled}
        testId="trigger-phrases-chips"
      />
      <div style={hintStyle}>
        Add a few ways someone might ask for this. It fires when at least half of a phrase's words
        appear in the user's message — so short, distinctive phrases work best. Enter or comma adds a
        phrase; Backspace removes the last.
      </div>

      <label style={labelStyle}>Don't trigger on</label>
      <ChipsInput
        values={notFor}
        onChange={onNotForChange}
        placeholder="Optional: a phrase that should NOT set it off"
        ariaLabel="Add exclusion phrase"
        variant="muted"
        chipStates={stateMap(result.exclusionHits, 'block')}
        disabled={disabled}
        testId="trigger-exclusions-chips"
      />
      <div style={hintStyle}>
        Blocks the fire when the message matches one of these (e.g. “don't high five me”). Stored
        today, but not yet enforced server-side — shown here as the intended behavior.
      </div>

      <div style={{
        background: '#101116', border: '1px dashed rgba(255,255,255,.14)', borderRadius: 6,
        padding: 10, marginTop: 12,
      }}>
        <label style={{ ...labelStyle, margin: '0 0 4px' }}>Test it — type what a user might say</label>
        <input
          data-testid="trigger-tester-input"
          value={utterance}
          onChange={(e) => setUtterance(e.target.value)}
          placeholder="give me a high five!"
          autoComplete="off"
          style={{
            width: '100%', boxSizing: 'border-box', background: '#17181d',
            border: '1px solid #ffffff12', borderRadius: 5, padding: '6px 10px',
            color: '#ececef', fontSize: 12, fontFamily: 'var(--m-font)', outline: 'none',
          }}
        />
        <div data-testid="trigger-tester-verdict" style={{ fontSize: 11, fontFamily: 'var(--m-font)', marginTop: 8, lineHeight: 1.6, minHeight: 17 }}>
          {verdict}
        </div>
      </div>
    </div>
  )
}
